import type { CatalogSkill } from "@bash/shared";
import { authedFetch, getRequiredAccessToken } from "./client";
import { installItem, findMyItemBySlug, deleteItem } from "./items";

/**
 * Thrown by uninstallSkillWithCascade when the skill is bundled into one or
 * more user-owned abilities. The caller should show a confirmation modal
 * listing the affected abilities and, if confirmed, retry the uninstall
 * with `confirmCascade: true`.
 *
 * NOTE: the new canonical DELETE endpoint does not gate on bundling (it
 * always cascades via FK). To preserve the legacy modal flow we keep the
 * error class + preflight on the client side — see uninstallSkillWithCascade.
 */
export class CascadeConfirmRequiredError extends Error {
  constructor(
    public readonly affected_abilities: Array<{ slug: string; name: string }>
  ) {
    super(
      `Cascade confirmation required for ${affected_abilities.length} abilities`
    );
    this.name = "CascadeConfirmRequiredError";
  }
}

// ---------------------------------------------------------------------------
// Skills catalog endpoint
//
// Backed by `GET /api/items/skill/catalog`. Returns scope=system|marketplace
// skills projected to `CatalogSkill` shape with computed state-from-caller
// so the UI can dim install buttons + group cards without extra round-trips.
// ---------------------------------------------------------------------------

/**
 * Browse the skills catalog (system + marketplace).
 *
 * - `q`        — full-text-ish search over slug + metadata.name + metadata.description
 * - `category` — exact-match filter on metadata.category
 */
export async function listSkillsCatalog(
  params: {
    q?: string;
    category?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<CatalogSkill[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("search", params.q);
  if (params.category) qs.set("category", params.category);
  if (typeof params.limit === "number") qs.set("limit", String(params.limit));
  if (typeof params.offset === "number")
    qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  const res = await authedFetch(`/api/items/skill/catalog${suffix}`);
  const body = (await res.json()) as { items: CatalogSkill[] };
  return body.items ?? [];
}

/** Install a marketplace/system skill — hits POST /api/items/skill/install/:slug. */
export async function installSkill(
  slug: string
): Promise<{ ok: true; skill_id: string }> {
  const result = await installItem("skill", slug);
  return { ok: true, skill_id: result.id };
}

/**
 * Uninstall a skill by slug.
 *
 * Legacy callers pass a slug; the new server endpoint takes an item id, so we
 * resolve via GET /api/items/skill/mine first, then DELETE /api/items/skill/:id.
 * Returns `{ ok: true }` on success (the new server returns 204 on success).
 */
export async function uninstallSkill(slug: string): Promise<{ ok: true }> {
  const mine = await findMyItemBySlug("skill", slug);
  if (!mine) throw new Error(`Skill not installed: ${slug}`);
  await deleteItem("skill", mine.id);
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Cascade-aware uninstall (client-side preflight + new id-based DELETE)
// ---------------------------------------------------------------------------

export interface SkillUsage {
  affected_abilities: Array<{ slug: string; name: string }>;
}

/**
 * "Usage" of an installed skill = the abilities that bundle it. Derived
 * client-side from the `bundling_ability_slugs` field on the user's item.
 *
 * Replaces the legacy `/api/skills/install/:slug/usage` endpoint. Names are
 * not surfaced by `/api/items/skill/mine`, so we send the slug as both
 * `slug` and `name`. The cascade-confirm modal stringifies whatever's here
 * for the user; keeping the field present preserves the contract.
 */
export async function getSkillUsage(slug: string): Promise<SkillUsage> {
  const mine = await findMyItemBySlug("skill", slug);
  if (!mine) return { affected_abilities: [] };
  return {
    affected_abilities: mine.bundling_ability_slugs.map((s) => ({
      slug: s,
      name: s,
    })),
  };
}

export interface UninstallSkillResult {
  ok: true;
  removed_from_abilities: string[];
}

/**
 * Cascade-aware uninstall.
 *
 * Behaviour mirrors the legacy contract:
 *   1. If the skill is bundled in 0 abilities → DELETE directly.
 *   2. If bundled in 1+ abilities and `confirmCascade !== true` → throw
 *      CascadeConfirmRequiredError so the caller can render a confirmation modal.
 *   3. If `confirmCascade === true` → DELETE (the server FK cascades for us).
 *
 * The legacy server's 409 + ?confirm_cascade=true round-trip is replicated
 * client-side because the new canonical DELETE always cascades unconditionally.
 */
export async function uninstallSkillWithCascade(
  slug: string,
  opts: { confirmCascade?: boolean } = {}
): Promise<UninstallSkillResult> {
  // Auth check so we surface "Not authenticated" with the same shape as before.
  await getRequiredAccessToken();

  const mine = await findMyItemBySlug("skill", slug);
  if (!mine) throw new Error(`Skill not installed: ${slug}`);

  const bundles = mine.bundling_ability_slugs;
  if (bundles.length > 0 && !opts.confirmCascade) {
    throw new CascadeConfirmRequiredError(
      bundles.map((s) => ({ slug: s, name: s }))
    );
  }

  await deleteItem("skill", mine.id);
  return { ok: true, removed_from_abilities: bundles };
}
